import { Fragment, startTransition, useMemo, useRef } from "react"
import type { BaseSyntheticEvent, FC, ReactElement, ReactNode, RefObject } from "react"

import { QueryClient, useQueryClient } from "@tanstack/react-query"
import { motion, useReducedMotion } from "framer-motion"
import capitalize from "lodash-es/capitalize"
import head from "lodash-es/head"
import isEmpty from "lodash-es/isEmpty"
import isNull from "lodash-es/isNull"
import map from "lodash-es/map"
import reject from "lodash-es/reject"
import slice from "lodash-es/slice"
import words from "lodash-es/words"
import type { Variants as TMotionVariants } from "motion-dom"
import { useMap } from "react-use"
import type { StableActions } from "react-use/lib/useMap"

import {
    Button,
    EButtonSize,
    EButtonVariant,
    ESessionsCounterColor,
    ESpecialChar,
    ETypographyAlignment,
    ETypographySize,
    ETypographyWeight,
    InfoBox,
    SessionsCounter,
    SpecialChars,
    Typography
} from "@/3514/components"
import { type TScreenBreakpoints, useIsNetworkFast, useScreenBreakpoints } from "@/3514/hooks"
import {
    AttendanceModal,
    EAttendanceModalOrigin,
    type TAttendanceModalProps,
    attendanceMarksQueryPrefetcher
} from "@/3514/modals/attendance"
import { type IAppState, useAppSelector } from "@/3514/store/app"
import { EModalId, type TModalDispatcher, useModalDispatcher } from "@/3514/store/modal"
import { withPressHandler } from "@/3514/utils"
import { type IUseTranslation, useTranslation } from "@/hooks"

import { ECoachDashboardQueryKey as EQueryKey } from "./coach.dashboard.api.config"
import { useCoachDashboardAttendancesSuspenseQuery as useSuspenseQuery } from "./coach.dashboard.api.queries.hooks"
import {
    type ICoachSessionWithoutAttendanceMarksDto as IDto,
    type ICoachSessionWithoutAttendanceMarksModel as IModel
} from "./coach.dashboard.api.types"
import styles from "./coach.dashboard.styles"
import uiConfig from "./coach.dashboard.ui.config"
import type { TCoachMarkAttendanceState as TState } from "./coach.dashboard.ui.types"
import uiUtils from "./coach.dashboard.ui.utils"

const { getAttendanceEntityRowItemDate: getDate, getAttendanceEntityRowItemTime: getTime } = uiUtils
const {
    markedAttendancesVirtualListConfig: { visibleItemCount, itemCountToRenderExpandButton }
} = uiConfig

const classes = styles.attendancesEntity

const CoachDashboardAttendancesEntity: FC = (): ReactElement => {
    const { t }: IUseTranslation = useTranslation()

    const { showModal }: TModalDispatcher = useModalDispatcher()

    const breakpoints: TScreenBreakpoints = useScreenBreakpoints()

    const queryClient: QueryClient = useQueryClient()

    const appState: IAppState = useAppSelector()

    const { data }: ReturnType<typeof useSuspenseQuery> = useSuspenseQuery()

    const [state, { set: updateState }]: [TState, StableActions<TState>] = useMap<TState>({
        withExpandButton: data.length > itemCountToRenderExpandButton,
        visibleItems: data.length > 0 ? slice<IModel>(data, 0, visibleItemCount) : [],
        isListExpanded: false
    })

    const containerSentinelRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null)

    const onButtonAction: ReturnType<typeof withPressHandler> = withPressHandler((): void =>
        startTransition((): void => {
            if (state.isListExpanded)
                containerSentinelRef?.current?.scrollIntoView({ block: "start", behavior: "smooth" })

            updateState("isListExpanded", !state.isListExpanded)
            updateState("visibleItems", state.isListExpanded ? slice<IModel>(data, 0, visibleItemCount) : data)
        })
    )

    const isNetworkFast: boolean = useIsNetworkFast()

    const isMotionReduced: boolean = useReducedMotion()

    const containerMotionVariants: TMotionVariants = useMemo(
        (): TMotionVariants => ({
            hidden: { opacity: 0 },
            visible: {
                opacity: 1,
                transition: { staggerChildren: isMotionReduced ? 0 : 0.15, delayChildren: isMotionReduced ? 0 : 0.1 }
            }
        }),
        [isMotionReduced]
    )

    const itemMotionVariants: TMotionVariants = useMemo(
        (): TMotionVariants => ({
            hidden: { opacity: 0, y: 10 },
            visible: { opacity: 1, y: 0, transition: { duration: isMotionReduced ? 0 : 0.3, ease: "easeOut" } }
        }),
        [isMotionReduced]
    )

    async function onListHoverAndFocus(e: BaseSyntheticEvent): Promise<void> {
        const elem: HTMLElement = e.target.closest("[data-index]")

        if (isNull(elem)) return void 0

        const elemIndex: number = Number(elem.getAttribute("data-index"))

        if (isNetworkFast) await attendanceMarksQueryPrefetcher({ qc: queryClient }, { sessionId: elemIndex })

        return void 0
    }

    async function handleModalSubmitSuccess(sessionId: number): Promise<void> {
        queryClient.setQueryData(EQueryKey.MarkedAttendances(), (dtos: IDto[]): IDto[] =>
            reject<IDto>(dtos, { session_id: sessionId })
        )

        startTransition((): void => {
            if (state.isListExpanded) updateState("visibleItems", reject<IModel>(state.visibleItems, { sessionId }))
            else
                updateState(
                    "visibleItems",
                    visibleItemCount === 1 ? [data[1]] : slice<IModel>(data, 1, visibleItemCount)
                )
        })
    }

    function onListButtonAction(e: BaseSyntheticEvent): void {
        startTransition((): void => {
            const elem: HTMLElement = e.target.closest("[data-index]")

            if (isNull(elem)) return void 0

            const sessionId: number = Number(elem.getAttribute("data-index"))
            const sessionTime: string = elem.getAttribute("data-start")

            showModal<TAttendanceModalProps>(EModalId.Attendance, {
                origin: EAttendanceModalOrigin.MissedAttendances,
                sessionId,
                sessionStartTime: sessionTime,
                async onSubmitSuccess(): Promise<void> {
                    await handleModalSubmitSuccess(sessionId)
                }
            })

            return void 0
        })
    }

    function renderBoxContent(): ReactNode {
        return (
            <div className={classes.container()}>
                <div className={classes.list()}>
                    <motion.div
                        variants={containerMotionVariants}
                        initial="hidden"
                        animate="visible"
                        role="tablist"
                        tabIndex={-1}
                        className={classes.listWrapper()}
                        onKeyDown={withPressHandler(onListButtonAction)}
                        onClick={withPressHandler(onListButtonAction)}
                        onMouseOver={onListHoverAndFocus}
                        onFocus={onListHoverAndFocus}
                    >
                        {map<IModel, ReactElement>(
                            state.visibleItems,
                            (s: IModel): ReactElement => (
                                <motion.div
                                    key={`cmas-${s.sessionId}`}
                                    className={classes.item({ isOpenSidebar: appState.isOpenSidebar })}
                                    variants={itemMotionVariants}
                                >
                                    <div className={classes.itemCol()}>
                                        <Typography
                                            text={getDate(s.sessionTime)}
                                            alignment={ETypographyAlignment.Center}
                                            weight={ETypographyWeight.Bold}
                                            size={
                                                breakpoints.xl || breakpoints.xxl
                                                    ? ETypographySize.Medium
                                                    : ETypographySize.Small
                                            }
                                        />
                                        <Typography
                                            text={getTime(s.sessionTime)}
                                            alignment={ETypographyAlignment.Center}
                                            size={
                                                breakpoints.xl || breakpoints.xxl
                                                    ? ETypographySize.Small
                                                    : ETypographySize.ExtraSmall
                                            }
                                        />
                                    </div>

                                    <div className={classes.itemCol()}>
                                        <Typography
                                            text={s.title}
                                            className={classes.itemTruncatedText()}
                                            alignment={ETypographyAlignment.Center}
                                            size={
                                                breakpoints.xl || breakpoints.xxl
                                                    ? ETypographySize.Medium
                                                    : ETypographySize.Small
                                            }
                                        />
                                        <Typography
                                            text={capitalize(s.sessionModality)}
                                            alignment={ETypographyAlignment.Center}
                                            size={
                                                breakpoints.xl || breakpoints.xxl
                                                    ? ETypographySize.Small
                                                    : ETypographySize.ExtraSmall
                                            }
                                        />
                                    </div>

                                    {Boolean(
                                        breakpoints.xs ||
                                            breakpoints.sm ||
                                            (breakpoints.md && appState.isOpenSidebar) ||
                                            (breakpoints.lg && appState.isOpenSidebar)
                                    ) ? null : (
                                        <div className={classes.itemCol()}>
                                            <Typography
                                                text={s.sessionType}
                                                className={classes.itemTruncatedText()}
                                                alignment={ETypographyAlignment.Center}
                                                size={
                                                    breakpoints.xl || breakpoints.xxl
                                                        ? ETypographySize.Medium
                                                        : ETypographySize.Small
                                                }
                                            />
                                            <Typography
                                                text={
                                                    <Fragment>
                                                        {s.duration}
                                                        {SpecialChars[ESpecialChar.EmptySpace]}
                                                        {t("common.min")}
                                                    </Fragment>
                                                }
                                                alignment={ETypographyAlignment.Center}
                                                size={
                                                    breakpoints.xl || breakpoints.xxl
                                                        ? ETypographySize.Small
                                                        : ETypographySize.ExtraSmall
                                                }
                                            />
                                        </div>
                                    )}

                                    <Button
                                        variant={EButtonVariant.Solid}
                                        data-index={s.sessionId}
                                        data-start={s.sessionTime}
                                        minWidth="fit-content"
                                    >
                                        {!(breakpoints.xl || breakpoints.xxl || breakpoints.lg)
                                            ? head(words(t("common.markAttendance")))
                                            : t("common.markAttendance")}
                                    </Button>
                                </motion.div>
                            )
                        )}
                    </motion.div>

                    {state.isListExpanded || data.length <= itemCountToRenderExpandButton ? null : (
                        <SessionsCounter dataLength={data.length} maxLinesCount={4} color={ESessionsCounterColor.Red} />
                    )}
                </div>

                {state.withExpandButton && (
                    <div className={classes.listBtn()}>
                        <Button
                            isCentered
                            variant={EButtonVariant.Outlined}
                            size={EButtonSize.Small}
                            onClick={onButtonAction}
                            onKeyDown={onButtonAction}
                            className={classes.listBtn()}
                            minWidth="fit-content"
                        >
                            {state.isListExpanded
                                ? t("coachSide.dashboard.notifications.attendances.button.expanded")
                                : t("coachSide.dashboard.notifications.attendances.button.collapsed", {
                                      count: data.length
                                  })}
                        </Button>
                    </div>
                )}
            </div>
        )
    }

    return (
        <Fragment>
            {isEmpty(data) ? null : (
                <Fragment>
                    <div ref={containerSentinelRef} className={classes.sentinel()} />

                    <InfoBox
                        variant="error"
                        className={classes.errorBoxContainer()}
                        title={t("coachSide.dashboard.notifications.attendances.title")}
                        content={renderBoxContent()}
                    />
                </Fragment>
            )}

            <AttendanceModal />
        </Fragment>
    )
}

export default CoachDashboardAttendancesEntity
